import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../services/api';
import type { AiCreditsSettings, AssistantSettings } from '../../types';
import { Select } from '../common/Select';
import { SkeletonDetail } from '../common/SkeletonDetail';

interface AssistantSettingsTabProps {
  value: AssistantSettings;
  aiCredits: AiCreditsSettings;
  onChange: (nextValue: AssistantSettings) => void;
  onAiCreditsChange: (nextValue: AiCreditsSettings) => void;
}

const MODEL_OPTIONS = [
  { value: 'gpt-4o-mini', label: 'gpt-4o-mini' },
  { value: 'gpt-4o', label: 'gpt-4o' },
  { value: 'gpt-4.1', label: 'gpt-4.1' },
  { value: 'gpt-4.1-mini', label: 'gpt-4.1-mini' },
  { value: 'gpt-4.1-nano', label: 'gpt-4.1-nano' },
];

function getErrorMessage(error: unknown): string {
  const apiError = error as { response?: { data?: { title?: string; detail?: string; message?: string } }; message?: string };
  return apiError.response?.data?.detail
    ?? apiError.response?.data?.title
    ?? apiError.response?.data?.message
    ?? apiError.message
    ?? 'Failed to load tenants';
}

export function AssistantSettingsTab({ value, aiCredits, onChange, onAiCreditsChange }: AssistantSettingsTabProps) {
  const [newOverrideTenantId, setNewOverrideTenantId] = useState('');
  const [newOverrideModel, setNewOverrideModel] = useState(MODEL_OPTIONS[0].value);

  const tenantsQuery = useQuery({
    queryKey: ['system-tenants'],
    queryFn: () => apiClient.getSystemTenants(),
  });

  const tenantMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const tenant of tenantsQuery.data ?? []) {
      map.set(tenant.id, tenant.name);
    }
    return map;
  }, [tenantsQuery.data]);

  const availableTenants = useMemo(
    () => (tenantsQuery.data ?? []).filter((tenant) => !value.tenantModelOverrides[tenant.id]),
    [tenantsQuery.data, value.tenantModelOverrides],
  );

  const updateSettings = (updates: Partial<AssistantSettings>) => {
    onChange({
      ...value,
      ...updates,
    });
  };

  const updateTenantOverride = (tenantId: string, model: string) => {
    updateSettings({
      tenantModelOverrides: {
        ...value.tenantModelOverrides,
        [tenantId]: model,
      },
    });
  };

  const removeTenantOverride = (tenantId: string) => {
    const updatedOverrides = { ...value.tenantModelOverrides };
    delete updatedOverrides[tenantId];
    updateSettings({ tenantModelOverrides: updatedOverrides });
  };

  const addTenantOverride = () => {
    if (!newOverrideTenantId) {
      return;
    }

    updateTenantOverride(newOverrideTenantId, newOverrideModel);
    setNewOverrideTenantId('');
  };

  return (
    <div className="space-y-6">
      <section className="rounded border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 mb-6">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Platform AI Budget</h3>
        <p className="mt-1 mb-4 text-sm text-gray-500 dark:text-gray-400">
          Set a platform-wide monthly AI budget based on actual Azure OpenAI cost. When reached, all AI features are paused until the next billing period.
          Leave empty for unlimited.
        </p>
        <div className="max-w-xs">
          <label htmlFor="assistant-global-ai-credit-cap" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Platform Monthly Cost Cap (Actual Azure Cost)
          </label>
          <input
            id="assistant-global-ai-credit-cap"
            type="number"
            step="0.01"
            min="0"
            value={aiCredits.globalMonthlyProviderCostCapUsd?.toString() ?? ''}
            onChange={(event) => {
              const rawValue = event.target.value.trim();
              if (rawValue.length === 0)
              {
                onAiCreditsChange({ ...aiCredits, globalMonthlyProviderCostCapUsd: null });
                return;
              }

              const parsed = Number.parseFloat(rawValue);
              if (!Number.isNaN(parsed) && parsed >= 0)
              {
                onAiCreditsChange({ ...aiCredits, globalMonthlyProviderCostCapUsd: parsed });
              }
            }}
            className="mt-1 w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
            placeholder="Unlimited (USD)"
          />
        </div>
      </section>

      <section className="rounded border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 mb-6">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Tier Default Credit Allocations</h3>
        <p className="mt-1 mb-4 text-sm text-gray-500 dark:text-gray-400">
          Configure default monthly credit allocations by tenant tier.
        </p>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {(['Trial', 'Starter', 'Professional', 'Enterprise'] as const).map((tier) => (
            <div key={tier}>
              <label htmlFor={`assistant-tier-default-${tier}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {tier}
              </label>
              <input
                id={`assistant-tier-default-${tier}`}
                type="number"
                min="0"
                value={(aiCredits.tierDefaults?.[tier] ?? 0).toString()}
                onChange={(event) => {
                  const rawValue = event.target.value.trim();
                  const parsed = rawValue.length > 0 ? Number.parseInt(rawValue, 10) : 0;
                  if (!Number.isNaN(parsed) && parsed >= 0) {
                    onAiCreditsChange({
                      ...aiCredits,
                      tierDefaults: {
                        ...(aiCredits.tierDefaults ?? {}),
                        [tier]: parsed,
                      },
                    });
                  }
                }}
                className="mt-1 w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
              />
            </div>
          ))}
        </div>
      </section>

      <section className="rounded border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 mb-6">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">AI Feature Defaults</h3>
        <p className="mt-1 mb-4 text-sm text-gray-500 dark:text-gray-400">
          These defaults apply to all tenants unless overridden at the tenant level.
        </p>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">AI Resolution Suggestions</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Allow tenants to use AI-powered incident analysis</p>
            </div>
            <span className="text-sm text-green-600 dark:text-green-400">Enabled by default</span>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">AI Beacon</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Allow tenants to use the AI assistant chatbot</p>
            </div>
            <span className="text-sm text-green-600 dark:text-green-400">Enabled by default</span>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Knowledge Sources</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Allow tenants to configure external knowledge grounding</p>
            </div>
            <span className="text-sm text-green-600 dark:text-green-400">Enabled by default</span>
          </div>
        </div>
      </section>

      <Select
        id="assistant-basic-tier-model"
        label="Basic Tier Model"
        options={MODEL_OPTIONS}
        value={value.basicTierModel}
        onChange={(model) => updateSettings({ basicTierModel: model })}
      />

      <Select
        id="assistant-premium-tier-model"
        label="Premium Tier Model"
        options={MODEL_OPTIONS}
        value={value.premiumTierModel}
        onChange={(model) => updateSettings({ premiumTierModel: model })}
      />

      <section className="rounded border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Tenant Model Overrides</h3>

        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
            <thead>
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-300">Tenant Name</th>
                <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-300">Model Override</th>
                <th className="px-3 py-2 text-right font-medium text-gray-700 dark:text-gray-300">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {Object.entries(value.tenantModelOverrides).length === 0 && (
                <tr>
                  <td colSpan={3} className="px-3 py-4 text-center text-gray-500 dark:text-gray-400">
                    No tenant-specific model overrides configured.
                  </td>
                </tr>
              )}
              {Object.entries(value.tenantModelOverrides).map(([tenantId, model]) => (
                <tr key={tenantId}>
                  <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{tenantMap.get(tenantId) ?? tenantId}</td>
                  <td className="px-3 py-2">
                    <select
                      value={model}
                      onChange={(event) => updateTenantOverride(tenantId, event.target.value)}
                      className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                    >
                      {MODEL_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => removeTenantOverride(tenantId)}
                      className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {tenantsQuery.isLoading && (
          <div className="mt-4">
            <SkeletonDetail />
          </div>
        )}

        {tenantsQuery.isError && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400">{getErrorMessage(tenantsQuery.error)}</p>
        )}

        {!tenantsQuery.isLoading && !tenantsQuery.isError && (
          <div className="mt-6 grid gap-3 rounded border border-gray-200 p-3 dark:border-gray-700 md:grid-cols-[1fr_220px_auto]">
            <div>
              <label htmlFor="assistant-override-tenant" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Tenant
              </label>
              <select
                id="assistant-override-tenant"
                value={newOverrideTenantId}
                onChange={(event) => setNewOverrideTenantId(event.target.value)}
                className="mt-1 w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
              >
                <option value="">Select tenant</option>
                {availableTenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </option>
                ))}
              </select>
            </div>
            <Select
              id="assistant-override-model"
              label="Model"
              options={MODEL_OPTIONS}
              value={newOverrideModel}
              onChange={setNewOverrideModel}
            />
            <div className="flex items-end">
              <button
                type="button"
                onClick={addTenantOverride}
                disabled={!newOverrideTenantId}
                className="w-full rounded-md bg-primary-700 px-4 py-2 text-sm font-medium text-white hover:bg-primary-800 disabled:cursor-not-allowed disabled:bg-gray-400"
              >
                Add Override
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
